'use client';

/**
 * CourseCardFull (T9a) — the atomic "full" course card. Used today inside
 * CoursePanel's collapsed single-result case (see course-panel.tsx) and
 * intended for later reuse by the voice pinned-card slot — hence the
 * `compact` prop (tightens paddings/type sizes for that smaller context)
 * rather than a second near-duplicate component.
 *
 * Field mapping and price logic carry over from the V2 course tile (the
 * renderer this supersedes): `unitPrice` is the live price, `listPrice` only
 * shown (struck through) when it's a genuine discount, `null` unitPrice
 * renders "Free".
 */

import React, { memo } from 'react';
import { CalendarDays, MapPin, Clock } from 'lucide-react';
import type { Course } from '@/types/course';
import { cn } from '@/lib/utils';
import { RegisterCTA } from '@/components/assistant/primitives/register-cta';
import { fmtDateRange, fmtWeekdaySpan } from '@/lib/course-date-format';

export interface CourseCardFullProps {
  course: Course;
  /** Tightens paddings/type sizes for smaller contexts (e.g. the voice
   *  pinned-card slot). Defaults to the full desktop/mobile card sizes. */
  compact?: boolean;
  className?: string;
}

export const CourseCardFull = memo(function CourseCardFull({
  course,
  compact = false,
  className,
}: CourseCardFullProps) {
  const {
    title,
    courseDescription,
    city,
    locationCity,
    state,
    eventStartDate,
    eventEndDate,
    listPrice,
    unitPrice,
    url,
  } = course;

  const discounted =
    unitPrice != null && listPrice != null && unitPrice < listPrice;
  const displayCity = city || locationCity;
  const registerHref = url || '#';

  return (
    <div
      className={cn(
        'flex flex-col rounded-[18px] border-[1px] border-[#f0e7da] bg-white',
        compact ? 'p-[16px]' : 'px-[16px] py-[16px] sm:px-[22px] sm:py-[20px]',
        className
      )}
    >
      {/* Header: title/description (left) + price (right) */}
      <div className="flex items-start justify-between gap-[16px]">
        <div className="min-w-0">
          <h3
            className={cn(
              'font-[family-name:var(--font-lora)] font-semibold leading-snug text-[#31364e]',
              compact ? 'text-[17px]' : 'text-[17px] sm:text-[20px]'
            )}
          >
            {title}
          </h3>
          {courseDescription && !compact && (
            <div className="mt-1 max-w-[320px] text-[14px] leading-snug text-[#6b7184]">
              {courseDescription}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end">
          <div className="flex items-baseline gap-1.5">
            {discounted && (
              <span className="text-[12px] text-[#9598a6] line-through">
                ${listPrice}
              </span>
            )}
            <span
              className={cn(
                'font-[family-name:var(--font-lora)] font-semibold leading-none text-[#E36001]',
                compact ? 'text-[16px]' : 'text-[18px] sm:text-[22px]'
              )}
            >
              {unitPrice != null ? `$${unitPrice}` : 'Free'}
            </span>
          </div>
          {unitPrice != null && (
            <span className="mt-0.5 text-[12.5px] text-[#9598a6]">
              per person
            </span>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="flex flex-col gap-2 border-y border-[#f1ece3] py-[13px] text-[13.5px] leading-tight text-[#31364e] sm:flex-row sm:flex-wrap sm:items-center sm:gap-[18px]">
        {eventStartDate && (
          <span className="flex items-center gap-1.5">
            <CalendarDays
              className="size-4 shrink-0 text-[#E36001]"
              strokeWidth={2}
            />
            {fmtDateRange(eventStartDate, eventEndDate)}
          </span>
        )}
        {displayCity && (
          <span className="flex items-center gap-1.5">
            <MapPin
              className="size-4 shrink-0 text-[#E36001]"
              strokeWidth={2}
            />
            {displayCity}
            {state && `, ${state}`}
          </span>
        )}
        {eventStartDate && (
          <span className="flex items-center gap-1.5">
            <Clock className="size-4 shrink-0 text-[#E36001]" strokeWidth={2} />
            {fmtWeekdaySpan(eventStartDate, eventEndDate)}
          </span>
        )}
      </div>

      {/* Actions — full-width single CTA on mobile, Register + Details on sm+ */}
      <div className="mt-[16px] flex gap-[12px] sm:hidden">
        <RegisterCTA href={registerHref} size={44} fullWidth>
          Register now
        </RegisterCTA>
      </div>
      <div className="mt-[16px] hidden gap-[12px] sm:flex">
        <RegisterCTA href={registerHref} size={46} className="flex flex-1">
          Register now
        </RegisterCTA>
        <a
          href={registerHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-[46px] shrink-0 items-center justify-center whitespace-nowrap rounded-[26px] border-[1px] border-[#eadfce] px-[20px] font-['Work_Sans'] text-[14.5px] font-semibold text-[#5b6072] no-underline transition-colors hover:bg-[#FFFCF8]"
        >
          Details
        </a>
      </div>
    </div>
  );
});

export default CourseCardFull;
